import React from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Button from "components/Button";
import Price from "components/Price";
import { BASE_API_URL } from "lib/constants";
import useCustomerClient from "hooks/use-customer-client";
import useCustomerUser from "hooks/use-customer-user";

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1.5rem auto 0;

  ${({ theme }) => theme.mq.md`
    flex-direction: row;
    justify-content: space-between;
  `};

  button {
    margin: 0 auto 1rem;

    ${({ theme }) => theme.mq.md`
      margin: 0 1rem 1rem;
    `};
  }
`;

const Amount = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.themeColors.secondary};
  ${({ policyViolation }) =>
    policyViolation &&
    css`
      color: ${({ theme }) => theme.themeColors.danger};
    `}
`;

const BoldMessage = styled.p`
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 1rem;
  border-bottom: 0.0625rem solid ${({ theme }) => theme.themeColors.lightGrey};
  ${({ policyViolation }) =>
    policyViolation &&
    css`
      color: ${({ theme }) => theme.themeColors.danger};
    `}
`;

const Message = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: ${({ theme }) => theme.themeColors.secondary};
`;

const Title = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
`;

const Fee = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Wrapper = styled.div`
  border: 0.0625rem solid ${({ theme }) => theme.themeColors.danger};
  border-radius: 0.75rem;
  margin: 3rem 0 0;
  padding: 2rem;
`;

export default function CancelAppointment(props) {
  const { callback, data, onClose } = props;
  const { attributes, id } = data;
  const {
    cancellationFee,
    cancellationNotes,
    cancellationTimeframe,
    startDatetime,
    status,
  } = attributes;
  const startTime = new Date(startDatetime);
  let cutoff = null;
  let message = null;
  let calculatedFee = 0;
  let policyViolation = false;
  const hours = cancellationTimeframe;
  if (hours && status === "approved") {
    cutoff = new Date(startTime - hours * 3600000);
    if (cutoff <= new Date()) {
      policyViolation = true;
      calculatedFee = cancellationFee;
    }
    message = (
      <Message>
        {cancellationNotes ? (
          <div>
            {cancellationNotes}
            <br />
            <br />
          </div>
        ) : null}
        {`We require ${hours} hours notice for cancellation or change of your
        appointment. Cancellations or changes after the cut-off period will
        incur a charge of `}
        <Price value={calculatedFee} /> as a cancellation fee.
      </Message>
    );
  }
  const client = useCustomerClient();
  const customerUser = useCustomerUser();
  const body = { id, customer_id: customerUser?.id };
  const cancelUrl = "/appointments/customer_cancellation";
  const cancellationMessage = () => {
    if (customerUser?.id) {
      return (
        <div>
          {message}
          <BoldMessage policyViolation={policyViolation}>
            {policyViolation && `You are cancelling within ${hours} hours!`}
            {!policyViolation &&
              `No cancellation fee ${cutoff ? "until" : ""} ${cutoff || ""}`}
          </BoldMessage>
          <Fee>
            <Title>Cancellation fee</Title>
            <Amount policyViolation={policyViolation}>
              <Price value={calculatedFee} />
            </Amount>
          </Fee>
        </div>
      );
    }
    return null;
  };
  return (
    <Wrapper>
      <Title>Are you sure you want to cancel this appointment?</Title>
      {cancellationMessage()}
      <ButtonWrapper>
        <Button
          outlineType="outline"
          colorType="secondary"
          onClick={() => {
            onClose();
          }}
        >
          Don't cancel
        </Button>
        <Button
          colorType="danger"
          type="submit"
          outlineType="outline"
          onClick={async () => {
            client.post(`${BASE_API_URL}${cancelUrl}`, body).then(() => {
              callback();
            });
          }}
        >
          Yes, cancel
        </Button>
      </ButtonWrapper>
    </Wrapper>
  );
}

CancelAppointment.propTypes = {
  callback: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.string,
    attributes: PropTypes.shape({
      cancellationFee: PropTypes.any,
      cancellationNotes: PropTypes.string,
      cancellationTimeframe: PropTypes.number,
      startDatetime: PropTypes.any,
      status: PropTypes.string,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
